import { GENERATION_TOOL, isToolSupportCustomModel } from 'src/data/generationTool';
import { ASSET_TYPE } from 'src/data/asset';
import { CHANNEL } from './channel';
import { RESOURCE_DOCUMENT } from './performance';

export const CUSTOM_MODEL = {
  facebookPrimaryText: ASSET_TYPE.facebookPrimaryText,
  // DS won’t be ready to support FB and Google Headlines for the upcoming release.
  // https://keywee.slack.com/archives/CFKBG7YM6/p1662290968918329
  facebookHeadline: ASSET_TYPE.facebookHeadline,
  googleDescription: ASSET_TYPE.googleDescription,
  googleHeadline: ASSET_TYPE.googleHeadline,
  linkedinSingleImageHeadline: ASSET_TYPE.linkedinSingleImageHeadline,
  linkedinSingleImageIntro: ASSET_TYPE.linkedinSingleImageIntro,
  emailSubject: ASSET_TYPE.emailSubject,
  emailColdOutreachBody: ASSET_TYPE.emailColdOutreachBody,
  emailPromotionalBody: ASSET_TYPE.emailPromotionalBody,
  // deprecated
  emailSubjectLine: ASSET_TYPE.emailSubjectLine,
  emailBody: 'email_body',
};

export const CUSTOM_MODEL_PROPS = {
  [CUSTOM_MODEL.facebookPrimaryText]: {
    description: 'Facebook Ads - Primary Texts',
    generationTool: GENERATION_TOOL.facebookPrimaryTextProduct,
    sources: [CHANNEL.facebook],
  },
  [CUSTOM_MODEL.facebookHeadline]: {
    description: 'Facebook Ads - Headlines',
    generationTool: GENERATION_TOOL.facebookHeadlineProduct,
    sources: [CHANNEL.facebook],
  },
  [CUSTOM_MODEL.googleDescription]: {
    description: 'Google Search Ad - Description',
    generationTool: GENERATION_TOOL.googleDescriptionProduct,
    sources: [CHANNEL.google],
  },
  [CUSTOM_MODEL.googleHeadline]: {
    description: 'Google Search Ad - Headline',
    generationTool: GENERATION_TOOL.googleHeadlineProduct,
    sources: [CHANNEL.google],
  },
  [CUSTOM_MODEL.linkedinSingleImageHeadline]: {
    description: 'LinkedIn Single Image - Headline',
    generationTool: GENERATION_TOOL.linkedinHeadlineSingleImage,
    sources: [CHANNEL.linkedin],
  },
  [CUSTOM_MODEL.linkedinSingleImageIntro]: {
    description: 'LinkedIn Single Image - Copy',
    generationTool: GENERATION_TOOL.linkedinIntroSingleImage,
    sources: [CHANNEL.linkedin],
  },
  [CUSTOM_MODEL.emailSubject]: {
    description: 'Email - Subject Line',
    generationTool: GENERATION_TOOL.emailSubject,
    sources: [CHANNEL.hubspot, RESOURCE_DOCUMENT.emailSubjectLineCsvResource],
  },
  [CUSTOM_MODEL.emailColdOutreachBody]: {
    description: 'Email - Cold',
    generationTool: GENERATION_TOOL.emailBody,
    sources: [CHANNEL.hubspot, RESOURCE_DOCUMENT.emailColdCsvResource],
  },
  [CUSTOM_MODEL.emailPromotionalBody]: {
    description: 'Email - Promotion',
    generationTool: GENERATION_TOOL.emailBody,
    sources: [CHANNEL.hubspot, RESOURCE_DOCUMENT.emailPromotionCsvResource],
  },
  [CUSTOM_MODEL.emailSubjectLine]: {
    description: 'Email - Subject Line',
    generationTool: GENERATION_TOOL.emailSubjectLine,
    sources: [],
  },
  [CUSTOM_MODEL.emailBody]: {
    description: 'Email Body',
    generationTool: GENERATION_TOOL.emailColdOutreachBody,
    sources: [],
  },
};

export const EMAIL_CUSTOM_MODELS = [
  CUSTOM_MODEL.emailSubject,
  CUSTOM_MODEL.emailColdOutreachBody,
  CUSTOM_MODEL.emailPromotionalBody,
];
export const getCustomModelSources = (customModel) =>
  CUSTOM_MODEL_PROPS[customModel]?.sources || [];

export const getCustomModelDescription = (customModel) =>
  CUSTOM_MODEL_PROPS[customModel]?.description;

export const isCustomModelEnabledForGenerationTool = (generationTool) =>
  isToolSupportCustomModel(generationTool);

export const CUSTOM_MODEL_STATUS = {
  created: 'created',
  validation_in_progress: 'validation_in_progress',
  validation_done: 'validation_done',
  training_in_progress: 'training_in_progress',
  error: 'error',
  archived: 'archived',
  active: 'active',
};

const PROCESSING_ERROR_TYPE = {
  notEnoughDatasetError: 'not_enough_dataset_error',
  notEnoughRelevantDataError: 'not_enough_relevant_data_error',
};

const PROCESSING_ERROR_TYPE_PROPS = {
  [PROCESSING_ERROR_TYPE.notEnoughDatasetError]: {
    message:
      'Not Enough Data: Please select additional data sets to create a reliable custom model.',
  },
  [PROCESSING_ERROR_TYPE.notEnoughRelevantDataError]: {
    message:
      'We were unable to create a reliable custom model from the data you selected. Please choose data from more campaigns for a more reliable model.',
  },
};

export const getProcessingErrorTypeMessage = (type) =>
  PROCESSING_ERROR_TYPE_PROPS[type]?.message ||
  'We were unable to create a custom model. Please try again later.';

const CUSTOM_MODEL_STATUS_PROPS = {
  [CUSTOM_MODEL_STATUS.active]: {
    label: 'Ready',
  },
  [CUSTOM_MODEL_STATUS.training_in_progress]: {
    label: 'Training',
  },
};

export const getCustomModelStatusLabel = (status) => CUSTOM_MODEL_STATUS_PROPS[status]?.label;

export const METRIC_OPTION = {
  conversion: 'conversion',
  clickThrough: 'click_through',
};

const METRIC_OPTION_PROPS = {
  [METRIC_OPTION.conversion]: {
    label: 'Conversions',
    description:
      'Predict how well your generated text is likely to cause people to click and complete an action.',
  },
  [METRIC_OPTION.clickThrough]: {
    label: 'Engagements',
    description:
      'Predict how well your generated text is likely to engage people and cause them to react.',
  },
};

const convertAssetTypeToCustomModel = (assetType) =>
  Object.values(CUSTOM_MODEL).find((customModel) => customModel === assetType) || null;

export const getMetricOptionLabel = (option, assetType) => {
  const customModel = convertAssetTypeToCustomModel(assetType);
  if (EMAIL_CUSTOM_MODELS.includes(customModel)) {
    if (option === METRIC_OPTION.conversion) {
      return 'Click rate';
    }
    if (option === METRIC_OPTION.clickThrough) {
      return 'Open rate';
    }
  }
  return METRIC_OPTION_PROPS[option]?.label;
};

export const getMetricOptionDescription = (option) => METRIC_OPTION_PROPS[option]?.description;

export const getGenerationToolsForCustomModel = (customModel) => {
  switch (customModel) {
    case CUSTOM_MODEL.facebookPrimaryText:
      return [
        GENERATION_TOOL.facebookPrimaryTextProduct,
        GENERATION_TOOL.facebookPrimaryTextContent,
        GENERATION_TOOL.facebookPrimaryTextProductRewrite,
      ];

    case CUSTOM_MODEL.facebookHeadline:
      return [GENERATION_TOOL.facebookHeadlineProduct, GENERATION_TOOL.facebookHeadlineContent];

    case CUSTOM_MODEL.googleDescription:
      return [
        GENERATION_TOOL.googleDescriptionProduct,
        GENERATION_TOOL.googleDescriptionProductRewrite,
      ];

    case CUSTOM_MODEL.googleHeadline:
      return [GENERATION_TOOL.googleHeadlineProduct, GENERATION_TOOL.googleHeadlineProductRewrite];

    case CUSTOM_MODEL.linkedinSingleImageHeadline:
      return [
        GENERATION_TOOL.linkedinHeadlineSingleImage,
        GENERATION_TOOL.linkedinHeadlineSingleImageRewrite,
      ];

    case CUSTOM_MODEL.linkedinSingleImageIntro:
      return [
        GENERATION_TOOL.linkedinIntroSingleImage,
        GENERATION_TOOL.linkedinIntroSingleImageRewrite,
      ];

    case CUSTOM_MODEL.emailSubject:
      return [GENERATION_TOOL.emailSubject];

    case CUSTOM_MODEL.emailColdOutreachBody:
    case CUSTOM_MODEL.emailPromotionalBody:
      return [GENERATION_TOOL.emailBody];

    default:
      return [];
  }
};
